import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { useCookies } from "react-cookie";

import {
    CustomerListType,
    DepartmentSimpleType,
    SecretarySimpleType,
    SelectedParametersType,
    UserType,
} from "../../../../types/types";

import {
    customerDefault,
    departmentDefault,
    secretaryDefault,
} from "../../../../consts/consts";

import { Toast } from "primereact/toast";

import findAllSecretariesByCustomerIdListResponse from "../../../../resources/Api/secretary/findAllSecretariesByCustomerIdListResponse";
import findAllDepartmentsByCustomerIdAndSecretaryIdListResponse from "../../../../resources/Api/department/findAllDepartmentsByCustomerIdAndSecretaryIdListResponse";
import customerFindAllListResponse from "../../../../resources/Api/customer/customerFindAllListResponse";

import {
    cpfFormatter,
    setToastRefToOpen,
} from "../../../../functions/functions";

import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    SelectChangeEvent,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import {
    ArrowBackOutlined,
    ArrowForwardOutlined,
    CloseOutlined,
    HelpOutlineOutlined,
    SaveOutlined,
    UndoOutlined,
} from "@mui/icons-material";

import updateDriverDepartment from "../../../../resources/Api/driver/updateDriverDepartmenr";
import { useRecoilValue } from "recoil";
import { userAtom } from "../../../../atoms/atoms";
import { isImpersonationLocked } from "../../../../utils/impersonation";

const TransferDriverComponent = () => {
    // atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    // funcionalities states
    const [isOnline, setIsOnline] = useState<boolean>(true);
    const [isOpenDetails, setIsOpenDetails] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<
        "warn" | "success" | "info" | "error" | undefined
    >("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);

    // states
    const [customersList, setCustomersList] = useState<Array<CustomerListType>>([]);
    const [secretariesList, setSecretariesList] = useState<Array<SecretarySimpleType>>([]);
    const [departmentsList, setDepartmentsList] = useState<Array<DepartmentSimpleType>>([]);
    const [departmentsTargetList, setDepartmentsTargetList] = useState<Array<DepartmentSimpleType>>([]);

    const [customer, setCustomer] = useState<CustomerListType>(customerDefault);
    const [secretary, setSecretary] = useState<SecretarySimpleType>(secretaryDefault);
    const [department, setDepartment] = useState<DepartmentSimpleType>(departmentDefault);

    const [secretaryTarget, setSecretaryTarget] = useState<SecretarySimpleType>(secretaryDefault);
    const [departmentTarget, setDepartmentTarget] = useState<DepartmentSimpleType>(departmentDefault);

    const [driversCpfList, setDriverCpfList] = useState<Array<string>>([]);
    const [isSharingDriver, setIsSharingDriver] = useState<boolean>(false);
    const [driverCpfCurrent, setDriverCpfCurrent] = useState<string>("");

    // cookies
    const [cookies] = useCookies(["selectedParameters", "token"]);

    // refs
    const toastRef = useRef<Toast>(null);
    const cpfRef = useRef<any>(null);

    const currentParameters: SelectedParametersType = useMemo(() => cookies.selectedParameters, [cookies.selectedParameters]);
    const [activeStep, setActiveStep] = useState<number>((!getCurrentUser.configs.isManager && !currentParameters?.isViewingAll) ? 1 : 0);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleClose = useCallback(() => {
        setLoading(false);
        setStatusMessage("warn");
        setIsToastVisible(false);
        setFieldsIsValid(true);
        setTextValidation("");
        setIsOpenDetails(false);
        setDriverCpfCurrent("");
        setDriverCpfList([]);
        setActiveStep((!getCurrentUser.configs.isManager && !currentParameters?.isViewingAll) ? 1 : 0);
        setCustomer(customerDefault);
        setSecretary(secretaryDefault);
        setDepartment(departmentDefault);
        setDepartmentTarget(departmentDefault);
        setSecretaryTarget(secretaryDefault);
        setIsSharingDriver(false);
    }, [getCurrentUser.configs.isManager, currentParameters?.isViewingAll]);

    useEffect(() => {
        const ref = toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [
        isOpenDetails,
        validationText,
        isFieldsValid,
        statusMessage,
        isToastVisible
    ]);

    const getCustomersList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        try {
            const response = await customerFindAllListResponse(token, currentParameters.customer.id);
            setCustomersList(response.data);
        } catch (error) {
            console.log(error);
            setCustomersList([]);
        }
    }, [cookies.token, currentParameters.customer.id]);

    useEffect(() => {
        if (getCurrentUser.configs.isManager) {
            getCustomersList();
        }
    }, [getCustomersList, getCurrentUser.configs.isManager]);

    const getSecretariesList = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");
        const customerId = !getCurrentUser.configs.isManager ? currentParameters?.customer?.id : customer.id;

        try {
            const response = await findAllSecretariesByCustomerIdListResponse(
                token,
                customerId
            );
            setSecretariesList(response.data);
        } catch (error) {
            console.log(error);
            setSecretariesList([]);
        }
    }, [cookies.token, customer?.id, currentParameters?.customer?.id, getCurrentUser.configs.isManager]);

    useEffect(() => {
        if (customer?.name || currentParameters?.customer?.id) {
            getSecretariesList();
        }
    }, [getSecretariesList, customer?.name, currentParameters?.customer.id]);

    const getDepartmentsList = useCallback(async (secretaryId: number, isTarget: boolean = false) => {
        const token = cookies.token || sessionStorage.getItem("token");
        const customerId = (!getCurrentUser.configs.isManager) ? currentParameters?.customer.id : customer.id;
        //const secretaryId = (!getCurrentUser.configs.isManager && !currentParameters?.isViewingAll) ? currentParameters?.secretary.id : secretary.id;

        try {
            const response =
                await findAllDepartmentsByCustomerIdAndSecretaryIdListResponse(
                    token,
                    customerId,
                    secretaryId
                );

            if (isTarget) {
                setDepartmentsTargetList(response.data);
            } else {
                setDepartmentsList(response.data);
            }
        } catch (error) {
            console.log(error);
            setDepartmentsList([]);
        }
    }, [cookies.token, customer?.id, secretary?.id, currentParameters?.secretary.id, currentParameters?.customer.id, getCurrentUser.configs.isManager]);

    useEffect(() => {
        if (secretary?.name || currentParameters?.secretary?.name) {
            const secretaryId = (!getCurrentUser.configs.isManager && !currentParameters?.isViewingAll) ? currentParameters?.secretary.id : secretary.id;
            getDepartmentsList(secretaryId);
        }
    }, [getDepartmentsList, secretary?.name, currentParameters?.secretary?.name, getCurrentUser.configs.isManager, currentParameters?.isViewingAll, currentParameters?.secretary.id, secretary.id]);

    const onUpdate = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        setLoading(true);

        const customerId = !getCurrentUser.configs.isManager ? currentParameters?.customer.id : customer.id;
        // const secretaryId = (!getCurrentUser.configs.isManager && !currentParameters?.isViewingAll) ? currentParameters?.secretary.id : secretary.id;
        const departmentId = (!getCurrentUser.configs.isManager && !currentParameters?.isViewingAll) ? currentParameters?.department.id : department.id;

        const secretaryTargetId = secretaryTarget.id;
        const departmentTargetId = departmentTarget.id;

        type DriverTransferType = {
            customerId: number;
            secretaryId: number;
            currentDepartmentId: number;
            targetDepartmentId: number;
            drivers: Array<string>;
            isSharing: boolean;
        };

        const data: DriverTransferType = {
            customerId: customerId,
            secretaryId: secretaryTargetId,
            currentDepartmentId: departmentId,
            targetDepartmentId: departmentTargetId,
            drivers: driversCpfList.map((it) => it.replace(/\D/g, "")),
            isSharing: isSharingDriver
        };

        try {
            await updateDriverDepartment(token, data);
            setTextValidation(
                `Registro${data.drivers.length > 1 ? "s" : ""} atualizado com sucesso.`
            );
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                setLoading(false);
                handleClose();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;

            const errors = data.reason.split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        secretaryTarget.id,
        currentParameters?.isViewingAll,
        getCurrentUser.configs.isManager,
        currentParameters?.customer.id,
        currentParameters?.department.id,
        isSharingDriver,
        driversCpfList,
        customer.id,
        secretary.id,
        department.id,
        departmentTarget.id,
        cookies.token,
        handleClose
    ]);

    const handleFormChange = useCallback(
        (value: string) => {
            const response = driversCpfList;
            // const cpfLength = 11;
            const registerNumberLength = 25;

            if (value.replace(/\D/g, "").length >= 3 && value.replace(/\D/g, "").length <= registerNumberLength && !response.includes(value)) {
                response.push(value);
                setDriverCpfList(response);
                setDriverCpfCurrent("");
            }
        },
        [driversCpfList]
    );

    const options = useMemo(() => {
        const departmentId = !getCurrentUser.configs.isManager && !currentParameters?.isViewingAll ? currentParameters?.department?.id : department.id;
        const result = departmentsTargetList.filter((item) => item.id !== (departmentId));
        return result;
    }, [getCurrentUser.configs.isManager, department.id, currentParameters?.department?.id, departmentsTargetList, currentParameters?.isViewingAll]);

    const steps = [
        {
            label: "Identificação",
            item: (
                <Paper className="trv-container tw-rounded-xl tw-border-0 tw-bg-white tw-shadow-none" elevation={0} sx={style}>
                    <Box className="col-md-12" style={{ marginBottom: 20 }}>
                        <Autocomplete
                            id="chip-label-select-customer"
                            className="input-select-customer"
                            style={{
                                width: "100%",
                                minHeight: "56px",
                                height: "56px",
                                backgroundColor: "#ffffff29",
                                borderColor: "lightgray",
                            }}
                            options={customersList}
                            value={!getCurrentUser.configs.isManager ? getCurrentUser.customer : customer}
                            onChange={(_, option) => {
                                setCustomer(option ?? customerDefault);
                            }}
                            disabled={!getCurrentUser.configs.isManager}
                            getOptionLabel={(option) => `${option.reducedName}`}
                            noOptionsText={"Nada encontrado"}
                            renderInput={(params) => (
                                <>
                                    <FormControl
                                        className="form-group"
                                        style={{ display: "flex" }}
                                    >
                                        <InputLabel
                                            id="country-label"
                                            required
                                            style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                            shrink
                                        >
                                            Cliente
                                        </InputLabel>
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            placeholder="Selecione um cliente"
                                            autoComplete="off"
                                            required
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                ...params.InputProps,
                                                notched: true,
                                                label: "Cliente",
                                                classes: {
                                                    notchedOutline: "custom-notch",
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </>
                            )}
                        />
                    </Box>

                    <Box className="col-md-12" style={{ marginBottom: 20 }}>
                        <Autocomplete
                            id="chip-label-select-secretary"
                            className="input-select-secretary"
                            style={{
                                width: "100%",
                                minHeight: "56px",
                                height: "56px",
                                backgroundColor: "#ffffff29",
                                borderColor: "lightgray",
                            }}
                            options={secretariesList}
                            value={secretariesList.find((a) => a.reducedName === (currentParameters?.secretary?.reducedName)) || secretary}
                            onChange={(_, option) => {
                                setSecretary(option ?? secretaryDefault);
                            }}
                            disabled={!getCurrentUser.configs.isManager && !currentParameters?.isViewingAll}
                            getOptionLabel={(option) => `${option.reducedName}`}
                            noOptionsText={"Nada encontrado"}
                            renderInput={(params) => (
                                <>
                                    <FormControl
                                        className="form-group"
                                        style={{ display: "flex" }}
                                    >
                                        <InputLabel
                                            id="country-label"
                                            required
                                            style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                            shrink
                                        >
                                            Secretaria
                                        </InputLabel>
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            placeholder="Selecione uma secretaria"
                                            autoComplete="off"
                                            required
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                ...params.InputProps,
                                                notched: true,
                                                label: "Secretaria",
                                                classes: {
                                                    notchedOutline: "custom-notch",
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </>
                            )}
                        />
                    </Box>
                </Paper>
            ),
        },
        {
            label: "Origem",
            item: (
                <Paper className="trv-container tw-rounded-xl tw-border-0 tw-bg-white tw-shadow-none" elevation={0} sx={style}>
                    <Box className="col-md-12" style={{ marginBottom: 20 }}>
                        <Autocomplete
                            id="chip-label-select-department"
                            className="input-select-department"
                            style={{
                                width: "100%",
                                minHeight: "56px",
                                height: "56px",
                                backgroundColor: "#ffffff29",
                                borderColor: "lightgray",
                            }}
                            options={departmentsList}
                            value={departmentsList.find((a) => a.reducedName === (currentParameters?.department?.reducedName)) || department}
                            onChange={(_, option) => {
                                setDepartment(option ?? departmentDefault);
                            }}
                            disabled={!getCurrentUser.configs.isManager && !currentParameters?.isViewingAll}
                            getOptionLabel={(option) => `${option.reducedName}`}
                            noOptionsText={"Nada encontrado"}
                            renderInput={(params) => (
                                <>
                                    <FormControl
                                        className="form-group"
                                        style={{ display: "flex" }}
                                    >
                                        <InputLabel
                                            id="country-label"
                                            required
                                            style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                            shrink
                                        >
                                            Departamento
                                        </InputLabel>
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            placeholder="Selecione um departamento"
                                            autoComplete="off"
                                            required
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                ...params.InputProps,
                                                notched: true,
                                                label: "Departamento",
                                                classes: {
                                                    notchedOutline: "custom-notch",
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </>
                            )}
                        />
                    </Box>

                    <Box className="col-md-12" style={{ margin: "15px 0px 15px 0px" }}>
                        <FormControl
                            className="form-group"
                            style={{ display: "flex", width: "100%" }}
                        >
                            <InputLabel id="demo-multiple-cpf-label" shrink disabled={false} required>
                                CPF/Matrícula
                            </InputLabel>
                            <TextField
                                name="cpf"
                                type="cpf"
                                ref={(ref) => (cpfRef.current = ref)}
                                autoComplete="off"
                                required
                                inputProps={{ maxLength: 25 }}
                                InputProps={{ label: "CPF/Matricula", notched: true }}
                                placeholder="Digite o CPF ou matrícula do motorista"
                                className="form-control driver-cpf"
                                value={driverCpfCurrent}
                                onKeyDown={(event) =>
                                    event.key === "Enter"
                                        ? (cpfRef.current.onchange = (e: any) =>
                                            handleFormChange(e.target.value))
                                        : null
                                }
                                onChange={(e) => setDriverCpfCurrent(e.target.value)}
                                disabled={false}
                            />
                            <Tooltip
                                title="Digite o CPF ou a matrícula do motorista e pressione Enter para inserir."
                                placement="top"
                                arrow
                            >
                                <IconButton
                                    className="visibility-password"
                                    aria-label="Ajuda para inserir motorista"
                                    edge="end"
                                    size="large"
                                    sx={{ color: "#006C77", cursor: "help" }}
                                >
                                    <HelpOutlineOutlined />
                                </IconButton>
                            </Tooltip>
                        </FormControl>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, gridColumn: "1 / -1" }}>
                        {driversCpfList.map((it) => {
                            return (
                                <Chip
                                    key={it}
                                    label={cpfFormatter(it)}
                                    onDelete={() => {
                                        const newList = driversCpfList.filter((item) => item !== it);
                                        setDriverCpfList(newList);
                                    }}
                                    style={{ margin: 1 }}
                                />
                            );
                        })}
                    </Box>
                </Paper>
            ),
        },
        {
            label: "Destino",
            item: (
                <Paper className="trv-container tw-rounded-xl tw-border-0 tw-bg-white tw-shadow-none" elevation={0} sx={style}>

                    <Box className="col-md-12" style={{ marginBottom: 20 }}>
                        <Autocomplete
                            id="chip-label-select-secretary"
                            className="input-select-secretary"
                            style={{
                                width: "100%",
                                minHeight: "56px",
                                height: "56px",
                                backgroundColor: "#ffffff29",
                                borderColor: "lightgray",
                            }}
                            options={secretariesList}
                            value={secretaryTarget}
                            onChange={(_, option) => {
                                setSecretaryTarget(option ?? secretaryDefault);
                                getDepartmentsList(option?.id!!, true);
                            }}
                            // disabled={!getCurrentUser.configs.isManager && !currentParameters?.isViewingAll}
                            getOptionLabel={(option) => `${option.reducedName}`}
                            noOptionsText={"Nada encontrado"}
                            renderInput={(params) => (
                                <>
                                    <FormControl
                                        className="form-group"
                                        style={{ display: "flex" }}
                                    >
                                        <InputLabel
                                            id="country-label"
                                            required
                                            style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                            shrink
                                        >
                                            Secretaria
                                        </InputLabel>
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            placeholder="Selecione uma secretaria"
                                            autoComplete="off"
                                            required
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                ...params.InputProps,
                                                notched: true,
                                                label: "Secretaria",
                                                classes: {
                                                    notchedOutline: "custom-notch",
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </>
                            )}
                        />
                    </Box>

                    <Box className="col-md-12" style={{ marginBottom: 20 }}>
                        <Autocomplete
                            id="chip-label-select-department"
                            className="input-select-department"
                            style={{
                                width: "100%",
                                minHeight: "56px",
                                height: "56px",
                                backgroundColor: "#ffffff29",
                                borderColor: "lightgray",
                            }}
                            options={options}
                            value={departmentTarget}
                            onChange={(_, option) => {
                                setDepartmentTarget(option ?? departmentDefault);
                            }}
                            getOptionLabel={(option) => `${option.reducedName}`}
                            noOptionsText={"Nada encontrado"}
                            renderInput={(params) => (
                                <>
                                    <FormControl
                                        className="form-group"
                                        style={{ display: "flex" }}
                                    >
                                        <InputLabel
                                            id="country-label"
                                            required
                                            style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                            shrink
                                        >
                                            Departamento
                                        </InputLabel>
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            placeholder="Selecione um departamento"
                                            autoComplete="off"
                                            required
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                ...params.InputProps,
                                                notched: true,
                                                label: "Departamento",
                                                classes: {
                                                    notchedOutline: "custom-notch",
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </>
                            )}
                        />
                    </Box>

                    {false && <Box className="col-md-12" style={{ margin: "15px 0px 5px 0px" }}>
                        <FormControlLabel
                            style={{ marginRight: 5, marginLeft: 5 }}
                            control={
                                <Checkbox
                                    disabled={!departmentTarget.reducedName}
                                    checked={isSharingDriver}
                                    onChange={(event) => {
                                        // if yes only share, if no, so is total tranfer
                                        setIsSharingDriver(event.target.checked);
                                    }}
                                />
                            }
                            label="Apenas compartilhar"
                        />
                    </Box>}
                </Paper>
            ),
        },
    ];

    return (
        <>
            <Toast
                ref={toastRef}
                position="center"
                onShow={() => {
                    setIsToastVisible(true);

                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            {!loading ? (
                <Box
                    className="container-fluid tw-w-full tw-rounded-xl tw-bg-white tw-px-0 tw-py-2"
                    sx={{ background: "#fff", borderRadius: 3, minHeight: 0, height: "auto", overflow: "visible" }}
                >
                    <Stepper
                        activeStep={activeStep}
                        orientation="vertical"
                        sx={{
                            background: "",
                            width: "100%",
                            maxWidth: "none",
                            margin: 0,
                            "& .MuiStepContent-root": { paddingLeft: { xs: 1, sm: 2 }, paddingRight: 0 },
                            "& .Mui-active": {
                                fill: "#003f4a",
                            },
                            "& .Mui-completed": {
                                fill: "#00b700",
                                color: "#ffff",
                            },
                        }}
                    >
                        {steps.map((step, index) => (
                            <Step key={step.label}>
                                <StepLabel
                                    optional={
                                        index === steps.length - 1 ? (
                                            <Typography variant="caption">
                                                Selecione o departamento de destino
                                            </Typography>
                                        ) : index === 0 ? (
                                            <Typography variant="caption">
                                                Selecione o cliente e a secretaria
                                            </Typography>
                                        ) : index === 1 ? (
                                            <Typography variant="caption">
                                                Selecione o departamento de origem e os motoristas a
                                                serem transferidos
                                            </Typography>
                                        ) : null
                                    }
                                >
                                    {step.label}
                                </StepLabel>
                                <StepContent>
                                    {step.item}
                                    <Paper
                                        className="tw-flex tw-w-full tw-flex-wrap tw-justify-end tw-gap-2 tw-border-t tw-border-slate-200 tw-pt-3"
                                        elevation={0}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            width: "100%",
                                            gap: 1,
                                            background: "transparent",
                                            marginTop: 2.5,
                                            paddingTop: 1.5,
                                            borderTop: "1px solid rgba(0,0,0,0.05)",
                                        }}
                                    >
                                        <Button
                                            type="button"
                                            style={{
                                                fontFamily: "Prometo",
                                                color: "#ffff",
                                                margin: 5,
                                            }}
                                            onClick={
                                                index === steps.length - 1 ? onUpdate : handleNext
                                            }
                                            className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                                            sx={{ order: 2, minWidth: 120, minHeight: 40, borderRadius: 2, backgroundColor: "#00B700", boxShadow: "none", "&:hover": { backgroundColor: "#009900", boxShadow: "0 6px 14px rgba(0,183,0,0.20)" } }}
                                            disabled={
                                                ((index === 0 && !currentParameters?.secretary.name && !secretary.name) ||
                                                (index === 1 && driversCpfList.length === 0) ||
                                                (index === 2 && !departmentTarget.name)) || (index === steps.length - 1 && isImpersonationLocked())
                                            }
                                        >
                                            {index === steps.length - 1 ? <SaveOutlined style={{ marginRight: 5 }} /> : <ArrowForwardOutlined style={{ marginRight: 5 }} />}
                                            <Typography
                                                className="forgot-password"
                                                style={{
                                                    fontFamily: "Prometo",
                                                    fontSize: "16px",
                                                    color: "#ffff",
                                                }}
                                            >
                                                {index === steps.length - 1 ? "Salvar" : "Próximo"}
                                            </Typography>
                                        </Button>

                                        <Button
                                            type="button"
                                            style={{
                                                fontFamily: "Prometo",
                                                color: "#ffff",
                                                margin: 5,
                                            }}
                                            onClick={handleBack}
                                            disabled={index === 0}
                                            className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                                            sx={{ order: 1, minWidth: 110, minHeight: 40, borderRadius: 2, boxShadow: "none", "&:hover": { boxShadow: "0 6px 14px rgba(220,53,69,0.18)" } }}
                                        >
                                            <UndoOutlined style={{ marginRight: 5 }} />
                                            <Typography
                                                className="forgot-password"
                                                style={{
                                                    fontFamily: "Prometo",
                                                    fontSize: "16px",
                                                    color: "#ffff",
                                                }}
                                            >
                                                {"Voltar"}
                                            </Typography>
                                        </Button>
                                    </Paper>
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>
                </Box>
            ) : (
                <div
                    style={{
                        width: "100%",
                        padding: 20,
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column",
                    }}
                >
                    <div className="tracker-loading">
                        <div className="ld-tracker">
                            <div></div>
                            <div></div>
                            <div></div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const style = {
    bgcolor: "#ffff",
    width: "100%",
    maxWidth: "none",
    margin: "8px 0 0",
    display: "grid",
    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
    gap: 1.5,
    border: "none",
    boxShadow: "none",
    borderRadius: 3,
    color: "black",
    justifyContent: "stretch",
    alignItems: "start",
    lineBreak: "anywhere",
    padding: { xs: 1, sm: 1.5 },
    fontFamily: "Prometo",
    "& > .col-md-12": {
        width: "100%",
        minWidth: 0,
        margin: "0 !important",
        padding: "0 !important",
    },
    "& .MuiAutocomplete-root, & .MuiFormControl-root": { width: "100%" },
    "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        backgroundColor: "#fff",
        transition: "box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease",
    },
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,63,74,0.24)", borderWidth: "1px" },
    "& .MuiOutlinedInput-root:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77", borderWidth: "1px" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#00B700" },
    "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,183,0,0.12)" },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00B700", borderWidth: "2px" },
};

export default TransferDriverComponent;
